import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { lightThemeColors } from "../../assets/styles/colors";
import CustomModal from "../Generic/CustomModal";
import CustomInput from "../Generic/CustomInput";
import Tooltip from "../Generic/Tooltip";
import { ReactComponent as BinIcon } from "../../assets/icons/bin-emotii.svg";
import { ReactComponent as PlusIcon } from "../../assets/icons/plusIcon.svg";

const MAX_DETAILS_LENGTH = 100;
const MIN_NAME_LENGTH = 3;

const createEmptyDraft = () => ({ name: "", details: "" });

const isDraftNameValid = (draft) => draft.name.trim().length >= MIN_NAME_LENGTH;

const isDraftNameTooShort = (draft) => draft.name.trim().length > 0 && !isDraftNameValid(draft);

const trimDraft = (draft) => ({ name: draft.name.trim(), details: draft.details.trim() });

const ApiKeyFormModal = ({
  isOpen,
  onClose,
  productTitle,
  keyLabel,
  subtitle,
  namePlaceholder,
  keyToEdit,
  onCreate,
  onUpdate,
}) => {
  const [keyDrafts, setKeyDrafts] = useState([createEmptyDraft()]);
  const draftsListRef = useRef(null);
  const previousDraftCount = useRef(1);

  useEffect(() => {
    if (!isOpen) return;
    setKeyDrafts(keyToEdit
      ? [{ name: keyToEdit.name || "", details: keyToEdit.details || "" }]
      : [createEmptyDraft()]);
  }, [isOpen, keyToEdit]);

  useEffect(() => {
    if (keyDrafts.length > previousDraftCount.current && draftsListRef.current) {
      draftsListRef.current.scrollTo({ top: draftsListRef.current.scrollHeight, behavior: "smooth" });
    }
    previousDraftCount.current = keyDrafts.length;
  }, [keyDrafts.length]);

  const handleDraftChange = (index, field, value) => {
    if (field === "details" && value.length > MAX_DETAILS_LENGTH) {
      return;
    }
    setKeyDrafts((prev) => prev.map((draft, i) => (i === index ? { ...draft, [field]: value } : draft)));
  };

  const handleAddDraft = () => {
    setKeyDrafts((prev) => [...prev, createEmptyDraft()]);
  };

  const handleRemoveDraft = (index) => {
    setKeyDrafts((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));
  };

  const handleSubmit = () => (
    keyToEdit ? onUpdate(trimDraft(keyDrafts[0])) : onCreate(keyDrafts.map(trimDraft))
  );

  const allDraftsValid = keyDrafts.every(isDraftNameValid);
  const hasMultipleDrafts = keyDrafts.length > 1;
  const addButtonText = hasMultipleDrafts ? `Add ${keyDrafts.length} keys` : "Add";

  return (
    <CustomModal
      isOpen={isOpen}
      onOpenChange={onClose}
      iconType="apiKey"
      iconAlignment="left"
      headerLayout="row"
      title={keyToEdit ? `Edit ${productTitle} Key` : `Add ${productTitle} Key${hasMultipleDrafts ? "s" : ""}`}
      subtitle={keyToEdit ? "Update the name and details for this key." : subtitle}
      titleAlignment="left"
      buttonLayout="row"
      showCloseButton={false}
      autoCloseOnSuccess={true}
      width={600}
      buttons={[
        {
          text: "Cancel",
          onClick: onClose,
          variant: "secondary"
        },
        {
          text: keyToEdit ? "Save" : addButtonText,
          action: handleSubmit,
          variant: "primary",
          disabled: !allDraftsValid,
          successMessage: keyToEdit
            ? `${keyLabel} updated successfully`
            : `${keyLabel}${hasMultipleDrafts ? "s" : ""} created successfully`
        }
      ]}
    >
      <ModalContent ref={draftsListRef}>
        {keyDrafts.map((draft, index) => {
          const fields = (
            <>
              <FieldSection>
                <CustomInput
                  value={draft.name}
                  onChange={(e) => handleDraftChange(index, "name", e.currentTarget.value)}
                  label={<>{hasMultipleDrafts || keyToEdit ? "Name" : "Enter a name"} <RequiredAsterisk>*</RequiredAsterisk></>}
                  labelClassName="font-bold"
                  placeholder={namePlaceholder}
                  className={isDraftNameTooShort(draft) ? "border-[#FDA29B] focus-visible:ring-[#FEE4E2]" : undefined}
                />
                {isDraftNameTooShort(draft) && (
                  <FieldError>Use at least {MIN_NAME_LENGTH} characters</FieldError>
                )}
              </FieldSection>

              {!hasMultipleDrafts && <Divider />}

              <FieldSection>
                <Label>Details <OptionalText>(optional)</OptionalText></Label>
                <CustomInput
                  value={draft.details}
                  onChange={(e) => handleDraftChange(index, "details", e.currentTarget.value)}
                  placeholder="Describe what this key is used for"
                />
                <CharCount>{draft.details.length}/{MAX_DETAILS_LENGTH} characters</CharCount>
              </FieldSection>
            </>
          );

          if (!hasMultipleDrafts) {
            return <React.Fragment key={index}>{fields}</React.Fragment>;
          }

          return (
            <KeyCard key={index}>
              <KeyCardHeader>
                <KeyCardTitle>
                  <KeyNumber>{index + 1}</KeyNumber>
                  Key {index + 1}
                </KeyCardTitle>
                <Tooltip content="Remove this key" side="top">
                  <RemoveKeyButton type="button" onClick={() => handleRemoveDraft(index)}>
                    <BinIcon />
                  </RemoveKeyButton>
                </Tooltip>
              </KeyCardHeader>
              {fields}
            </KeyCard>
          );
        })}

        {!keyToEdit && (
          <AddAnotherKeyButton type="button" onClick={handleAddDraft}>
            <PlusIcon />
            Add another key
          </AddAnotherKeyButton>
        )}
      </ModalContent>
    </CustomModal>
  );
};

export default ApiKeyFormModal;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  max-height: min(60vh, 520px);
  overflow-y: auto;
  padding: 4px 12px 4px 4px;
  margin-left: -4px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #9ca3af;
  }
`;

const KeyCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  border: 1px solid #EAECF0;
  border-radius: 12px;
  background: #FCFCFD;
  flex-shrink: 0;
  transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;

  &:focus-within {
    border-color: #D6BBFB;
    background: #FFFFFF;
    box-shadow: 0 0 0 4px #F4EBFF;
  }
`;

const KeyCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: -4px -4px 0 0;
`;

const KeyCardTitle = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #344054;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
`;

const KeyNumber = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #F4EBFF;
  color: #6941C6;
  font-size: 12px;
  font-weight: 600;
`;

const AddAnotherKeyButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  flex-shrink: 0;
  padding: 10px 16px;
  border: 1px dashed #D6BBFB;
  border-radius: 12px;
  background: #FFFFFF;
  color: #6941C6;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s;

  &:hover {
    background: #F9F5FF;
    border-color: #B692F6;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const CharCount = styled.p`
  color: #667085;
  font-size: 12px;
  font-weight: 400;
  text-align: right;
  margin: 0;
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background: #D0D5DD;
  flex-shrink: 0;
`;

const FieldSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const Label = styled.label`
  color: ${lightThemeColors.darkColor};
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
`;

const RequiredAsterisk = styled.span`
  color: #d92d20;
`;

const FieldError = styled.p`
  color: #D92D20;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  margin: 0;
`;

const OptionalText = styled.span`
  color: #667085;
  font-weight: 400;
`;

const RemoveKeyButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  transition: all 0.2s;

  &:hover {
    background: #fef2f2;
    color: #ef4444;
  }

  svg {
    width: 16px;
    height: 16px;
    fill: none !important;
    stroke: currentColor !important;
    stroke-width: 1.5 !important;
  }

  svg path {
    fill: none !important;
    stroke: currentColor !important;
    stroke-width: 1.5 !important;
  }
`;
